import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { ProgressBarModule } from 'primeng/progressbar';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService, MessageService } from 'primeng/api';
import { GoogleAnalyticsService } from 'ngx-google-analytics';
import { finalize, forkJoin } from 'rxjs';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { PermissionService } from '../../security/permission.service';
import { app_permissions } from '../../security/app-permissions';
import { Shipment } from '../models/shipment';
import { ShipmentDelivery } from '../models/delivery/shipment-delivery.model';
import { PayloadItemView } from '../../models/meta-params/payload-item-view';
import { ShipmentJobStatus } from '../models/delivery/shipment-job.status';
import {
    ShipmentDeliveryOperationStatus,
    ShipmentOperationTimelineEvent
} from '../models/workflow/shipment-workflow.model';
import { ShipmentAccountsPostingResult, ShipmentService } from '../shipment.service';
import { ShipmentExperienceProfile } from './shipment-experience.model';
import { ShipmentExperienceService } from './shipment-experience.service';
import { ShipmentModePreferenceService } from './shipment-mode-preference.service';
import { calculateDeliveryOperationProgress } from '../utils/delivery-operation-progress.util';
import { TranslocoPipe } from '@jsverse/transloco';
import { LocaleService } from '../../i18n/locale.service';
import { ShipmentCommercialPartyCorrectionComponent } from '../commercial-party-correction/shipment-commercial-party-correction.component';
import { ShipmentAccountsReviewDialogComponent } from '../accounts-posting-review/shipment-accounts-review-dialog.component';

@Component({
    selector: 'app-simple-shipment-view',
    standalone: true,
    templateUrl: './simple-shipment-view.component.html',
    styleUrls: ['./simple-shipment-view.component.css'],
    imports: [
        CommonModule,
        RouterModule,
        ButtonModule,
        ProgressBarModule,
        ProgressSpinnerModule,
        TagModule,
        ToastModule,
        ConfirmDialogModule,
        TranslocoPipe,
        PageHeroComponent,
        PageShellComponent,
        ShipmentCommercialPartyCorrectionComponent,
        ShipmentAccountsReviewDialogComponent
    ],
    providers: [ConfirmationService, MessageService]
})
export class SimpleShipmentViewComponent implements OnInit {
    @ViewChild(ShipmentCommercialPartyCorrectionComponent) private partyCorrectionDialog?: ShipmentCommercialPartyCorrectionComponent;
    loading = true;
    error?: string;
    shipment?: Shipment;
    profile?: ShipmentExperienceProfile;
    selectedDeliveryId?: number;
    operationStatus?: ShipmentDeliveryOperationStatus;
    timelineLoading = false;
    postingToAccounts = false;
    accountsReviewVisible = false;
    accountsPostingResult?: ShipmentAccountsPostingResult;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private shipmentService: ShipmentService,
        private experienceService: ShipmentExperienceService,
        private modePreference: ShipmentModePreferenceService,
        private permissionService: PermissionService,
        private analytics: GoogleAnalyticsService,
        private confirmationService: ConfirmationService,
        private messageService: MessageService,
        private localeService: LocaleService
    ) {}

    ngOnInit(): void {
        const shipmentId = Number(this.route.snapshot.paramMap.get('shipmentId'));
        if (!shipmentId) {
            this.loading = false;
            this.error = 'Shipment not found.';
            return;
        }
        forkJoin({
            profile: this.experienceService.effective(),
            shipment: this.shipmentService.getShipmentById(shipmentId)
        }).pipe(finalize(() => this.loading = false)).subscribe({
            next: result => {
                this.profile = result.profile;
                if (this.modePreference.get() === 'ADVANCED') {
                    this.router.navigate(['/shipment/view', shipmentId], { replaceUrl: true });
                    return;
                }
                this.shipment = result.shipment;
                this.selectedDeliveryId = result.shipment.deliveries?.[0]?.deliveryId;
                this.loadSelectedDeliveryOperations();
            },
            error: error => this.error = error?.error?.message || 'The shipment could not be loaded.'
        });
    }

    get selectedDelivery(): ShipmentDelivery | undefined {
        return this.shipment?.deliveries?.find(delivery => delivery.deliveryId === this.selectedDeliveryId)
            ?? this.shipment?.deliveries?.[0];
    }

    selectDelivery(delivery: ShipmentDelivery): void {
        this.selectedDeliveryId = delivery.deliveryId;
        this.loadSelectedDeliveryOperations();
    }

    switchToAdvanced(): void {
        if (!this.shipment?.shipmentId) {
            return;
        }
        this.analytics.gtag('event', 'shipment_mode_switch', {
            from_mode: 'SIMPLE',
            to_mode: 'ADVANCED',
            surface: 'VIEW'
        });
        this.modePreference.set('ADVANCED');
        this.router.navigate(['/shipment/view', this.shipment.shipmentId]);
    }

    show(fieldKey: string): boolean {
        return this.profile?.fieldRules.find(rule => rule.fieldKey === fieldKey)?.displayMode !== 'HIDDEN';
    }

    permits(action: string): boolean {
        const configured = this.profile?.permittedActions ?? [];
        const profileAllows = configured.length
            ? configured.includes(action)
            : ['VIEW_DELIVERY', 'REASSIGN', 'SWITCH_TO_ADVANCED'].includes(action);
        if (!profileAllows) {
            return false;
        }
        if (action === 'REASSIGN') {
            return this.permissionService.hasPermission(app_permissions.shipments.deliveries.GENERATE);
        }
        if (action === 'VIEW_DELIVERY') {
            return this.permissionService.hasPermission(app_permissions.shipments.deliveries.view);
        }
        return true;
    }

    statusLabel(value?: string | ShipmentJobStatus): string {
        if (!value) {
            return 'Awaiting execution';
        }
        return String(value).replaceAll('_', ' ').toLowerCase()
            .replace(/\b\w/g, letter => letter.toUpperCase());
    }

    progressPercent(delivery?: ShipmentDelivery): number {
        return calculateDeliveryOperationProgress(
            delivery,
            delivery?.deliveryId === this.selectedDelivery?.deliveryId ? this.operationStatus : undefined,
            Boolean(delivery?.deliveryDriver || delivery?.deliveryVehicle)
        ).percent;
    }

    progressCaption(delivery?: ShipmentDelivery): string {
        return calculateDeliveryOperationProgress(
            delivery,
            delivery?.deliveryId === this.selectedDelivery?.deliveryId ? this.operationStatus : undefined,
            Boolean(delivery?.deliveryDriver || delivery?.deliveryVehicle)
        ).caption;
    }

    nextActionLabel(): string {
        if (this.canPostToAccounts()) {
            return this.isPostedToAccounts() ? 'Update Accounts'
                : this.localeService.translate('pages.shipmentDetail.actions.postToAccounts');
        }
        if (this.isPostedToAccounts()) {
            return this.localeService.translate('pages.shipmentDetail.actions.openFinancials');
        }
        if (!this.shipment?.deliveries?.length) {
            return this.permits('REASSIGN') ? 'Assign driver' : 'Open advanced workspace';
        }
        if (this.selectedDelivery?.jobStatus === ShipmentJobStatus.DRIVER_JOB_COMPLETED) {
            return 'Review POD';
        }
        return 'Open delivery';
    }

    nextActionRoute(): any[] {
        if (!this.shipment?.shipmentId) {
            return ['/shipment'];
        }
        if (!this.shipment.deliveries?.length) {
            return this.permits('REASSIGN')
                ? ['/shipment/delivery', this.shipment.shipmentId, 'generate']
                : ['/shipment/view', this.shipment.shipmentId, 'advanced'];
        }
        return this.permits('VIEW_DELIVERY')
            ? ['/shipment/delivery/view', this.selectedDelivery?.deliveryId]
            : ['/shipment/view', this.shipment.shipmentId, 'advanced'];
    }

    canPostToAccounts(): boolean {
        return ['COMPLETED', 'POSTED_TO_ACCOUNTS'].includes(String(this.shipment?.shipmentStatus))
            && this.permissionService.hasPermission(app_permissions.shipments.finance.post);
    }

    canCorrectBillToParty(): boolean {
        return !!this.shipment?.shipmentId
            && !['POSTED_TO_ACCOUNTS', 'INVOICED', 'CLOSED', 'CANCELLED'].includes(this.shipment.shipmentStatus)
            && this.permissionService.hasPermission(app_permissions.shipments.party_correct);
    }

    openBillToPartyCorrection(): void {
        if (this.canCorrectBillToParty()) this.partyCorrectionDialog?.open();
    }

    onBillToPartyCorrected(): void {
        const shipmentId = this.shipment?.shipmentId;
        if (!shipmentId) return;
        this.shipmentService.getShipmentById(shipmentId).subscribe({
            next: shipment => {
                this.shipment = shipment;
                this.messageService.add({ severity: 'success', summary: 'Bill-to party corrected',
                    detail: 'The shipment now uses the selected CRM party.' });
            },
            error: () => this.messageService.add({ severity: 'warn', summary: 'Correction saved',
                detail: 'Refresh the shipment to view its new bill-to party.' })
        });
    }

    isPostedToAccounts(): boolean {
        return this.shipment?.shipmentStatus === 'POSTED_TO_ACCOUNTS';
    }

    postToAccounts(): void {
        if (!this.shipment?.shipmentId || !this.canPostToAccounts() || this.postingToAccounts) {
            return;
        }
        this.accountsReviewVisible = true;
    }

    confirmAccountsPost(): void {
        if (!this.accountsReviewVisible || !this.shipment?.shipmentId || this.postingToAccounts) return;
                this.postingToAccounts = true;
                this.shipmentService.postShipmentToAccounts(this.shipment!.shipmentId!)
                    .pipe(finalize(() => this.postingToAccounts = false))
                    .subscribe({
                        next: result => {
                            this.accountsPostingResult = result;
                            this.accountsReviewVisible = false;
                            this.shipment!.shipmentStatus = result.shipmentStatus as any;
                            this.shipment!.workflowState = result.shipmentStatus;
                            this.messageService.add({
                                severity: 'success',
                                summary: this.localeService.translate('pages.shipmentDetail.actions.postToAccountsSuccess'),
                                detail: this.localeService.translate('pages.shipmentDetail.actions.postToAccountsSummary', {
                                    bulkCosts: result.bulkCostIds?.length ?? 0,
                                    receivables: result.receivableIds?.length ?? 0,
                                    payables: result.payableIds?.length ?? 0
                                }),
                                life: 8000
                            });
                            if (result.channelOutcomes?.['FINANCE_RECONCILIATION']) {
                                this.messageService.add({ severity: 'warn', summary: 'Finance reconciliation',
                                    detail: result.channelOutcomes['FINANCE_RECONCILIATION'], life: 12000 });
                            }
                        },
                        error: error => this.messageService.add({
                            severity: 'error',
                            summary: this.localeService.translate('pages.shipmentDetail.actions.postToAccountsError'),
                            detail: error?.error?.message || error?.message
                                || this.localeService.translate('pages.shipmentDetail.actions.postToAccountsRetry')
                        })
                    });
    }

    openFinancials(): void {
        if (this.shipment?.shipmentId) {
            this.router.navigate(['/shipment/view', this.shipment.shipmentId], {
                queryParams: { tab: 'financials' }
            });
        }
    }

    driverName(delivery?: ShipmentDelivery): string {
        return delivery?.deliveryDriver?.name || delivery?.deliveryDriver?.legalName || 'Not assigned';
    }

    vehicleLabel(delivery?: ShipmentDelivery): string {
        const vehicle = delivery?.deliveryVehicle;
        return vehicle?.plateNumber || vehicle?.registrationNumber || 'Not assigned';
    }

    cargoQuantity(delivery?: ShipmentDelivery): string {
        const lines = delivery?.payloadItemList?.length
            ? delivery.payloadItemList
            : this.shipment?.payloadItemList ?? [];
        const total = lines.reduce((sum, line) => sum + Number(line.itemQuantity?.quantity ?? 0), 0);
        const uom = lines[0]?.itemQuantity?.uom?.shortName ?? '';
        return `${total || '—'} ${uom}`.trim();
    }

    payloadTotalWeightLabel(item?: PayloadItemView): string {
        const totalWeight = Number(item?.itemWeight ?? 0) * Number(item?.itemQuantity?.quantity ?? 0);
        const unit = item?.weightUnit?.shortName || '';
        return totalWeight > 0 ? `${Number(totalWeight.toFixed(3))} ${unit}`.trim() : 'Weight pending';
    }

    get timelineEvents(): ShipmentOperationTimelineEvent[] {
        return [...(this.operationStatus?.timelineEvents ?? [])]
            .sort((left, right) => this.eventTimeValue(right) - this.eventTimeValue(left))
            .slice(0, 5);
    }

    get exceptionEvents(): ShipmentOperationTimelineEvent[] {
        return (this.operationStatus?.timelineEvents ?? []).filter(event =>
            event.overridden
            || (!!event.gateResult && !['PASSED', 'PASS', 'SUCCESS'].includes(event.gateResult.toUpperCase()))
            || event.title?.toLowerCase().includes('exception')
            || event.description?.toLowerCase().includes('exception'));
    }

    eventLabel(event: ShipmentOperationTimelineEvent): string {
        return event.title || this.statusLabel(event.nextState || event.level || 'Workflow update');
    }

    private loadSelectedDeliveryOperations(): void {
        const shipmentId = this.shipment?.shipmentId;
        const deliveryId = this.selectedDeliveryId;
        this.operationStatus = undefined;
        if (!shipmentId || !deliveryId) {
            return;
        }
        this.timelineLoading = true;
        this.shipmentService.getDeliveryOperationStatus(shipmentId, deliveryId)
            .pipe(finalize(() => this.timelineLoading = false))
            .subscribe({
                next: status => this.operationStatus = status,
                error: () => this.operationStatus = undefined
            });
    }

    private eventTimeValue(event: ShipmentOperationTimelineEvent): number {
        if (!event.eventAt) {
            return 0;
        }
        const value = new Date(event.eventAt).getTime();
        return Number.isFinite(value) ? value : 0;
    }
}
